import {
    type PropsWithChildren as PWC,
    createContext,
    useContext,
} from 'react';

import type { AuthProviderProps } from '@myphx-engineering/auth/client';
import type { TelemetryProviderProps } from '@myphx-engineering/instrumentation/react';

import { routes } from '../components/router/routes';

export type AppConfig = {
    navLogoUrl: string;
    routerUrl: string;
    env: 'development' | 'production';
    authentication: AuthProviderProps;
    telemetry: TelemetryProviderProps;
};

const AppConfigContext = createContext<AppConfig | null>(null);

/**
 * A proxy is used for local development here to avoid CORS issues. The proxy
 * settings can be found in the vite.config.ts.
 */
const routerUrl =
    import.meta.env.VITE_ENV === 'development'
        ? '/gql'
        : import.meta.env.VITE_GRAPH_URL;

const authConfig: AuthProviderProps = {
    authUrl: import.meta.env.VITE_AUTH_URL,
    clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
    redirectUri: import.meta.env.VITE_AUTH_REDIRECT_URI,
    rootUrl: routes.root,
    tokenUrl: import.meta.env.VITE_AUTH_TOKEN_URL,
    disableHeartbeat: true,
    autoLogout: 30 * 60 * 1000, // 30 minutes
    defaultState: {
        brandId: 'LillyDirect',
    },
};

const telemetryConfig: TelemetryProviderProps = {
    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
    instrumentationKey: import.meta.env
        .VITE_APPLICATION_INSIGHTS_CONNECTION_STRING,
    cloudRole: 'ux-lilly-direct', // Does this need to be initialized in App Insights?
};

const DEFAULT_CONFIG: AppConfig = {
    navLogoUrl: '/static/img/lilly-logo.svg',
    routerUrl,
    env:
        import.meta.env.VITE_ENV === 'production'
            ? 'production'
            : 'development',
    authentication: authConfig,
    telemetry: telemetryConfig,
};

export const AppConfigProvider = ({ children }: PWC) => {
    const ctx: AppConfig = DEFAULT_CONFIG;

    return (
        <AppConfigContext.Provider value={ctx}>
            {children}
        </AppConfigContext.Provider>
    );
};

export const useAppConfig = (): AppConfig => {
    const ctx = useContext(AppConfigContext);

    if (ctx === null) {
        throw new Error(
            'useAppConfig() cannot be called outside an AppConfigProvider'
        );
    }

    return ctx;
};
